// ChoosePlanAndPay.jsx
import React, {
  useEffect,
  useState,
  useCallback,
  useMemo,
  useRef,
} from "react";
import { Card, Button, Row, Col } from "react-bootstrap";
import { useSelector, useDispatch } from "react-redux";
import { fetchSubscriptionPackages } from "../actions/userActions";
import { useNavigate, useParams } from "react-router-dom";
import StripeCreatePayMethod from "../components/StripeCreatePayMethod";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCircleLeft, faCircleCheck } from "@fortawesome/free-solid-svg-icons";
import {
  resetCreatePaymentIntent,
  resetConfirmSubscription,
} from "../actions/restaurantActions";
import LoaderComponent from "../components/LoaderComponent";
import styled, { css } from "styled-components";
import {
  Container,
  CardHeader,
  CardHeaderTitle,
} from "../components/DefaultImports";
import { ConfirmationModal } from "../components/ConfirmationModal";
import {
  createUpgradeIntent,
  activateStarterPack,
  fetchSubscriptionStatus,
} from "../actions/paymentActions";

/* --------- CONFIG ---------- */
const FEATURE_PREVIEW_COUNT = 7;

/* ---------- Paleta de cores cíclica ---------- */
const palette = ["#9FBBC1", "#EE754C", "#99B0AD"];
const getPlanColor = (subscription_number) =>
  palette[(subscription_number - 1) % palette.length];

/* ---------- Styled-components ---------- */
const ButtonPlans = styled.div`
  padding: 10px 24px;
  border-radius: 30px;
  font-size: 16px;
  font-weight: 600;
  background-color: ${(props) => (props.isSelected ? "#ee754c" : "#ffffff")};
  color: ${(props) => (props.isSelected ? "#ffffff" : "#ee754c")};
  border: 2px solid #ee754c;
  cursor: pointer;
  transition: all 0.3s ease-in-out;
  margin: 5px;
  box-shadow: ${(props) =>
    props.isSelected ? "0 6px 12px rgba(238,117,76,0.2)" : "none"};

  &:hover {
    background-color: #ee561b;
    color: #ffffff;
  }
`;

const PlanCard = styled(Card)`
  border-radius: 18px;
  border: 1px solid #dee2e6;
  background-color: #ffffff;
  padding: 20px 16px;
  text-align: center;
  transition:
    transform 0.25s ease,
    box-shadow 0.25s ease;
  min-height: 100%;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.04);
  display: flex;
  flex-direction: column;

  &:hover {
    transform: translateY(-3px);
  }

  ${(p) =>
    p.$selected &&
    css`
      border-color: #ee754c;
      box-shadow: 0 6px 16px rgba(238, 117, 76, 0.18);
    `}
`;

const ActiveBadge = styled.div`
  display: inline-block;
  background: #28a745;
  color: #fff;
  font-weight: 700;
  font-size: 11px;
  padding: 4px 10px;
  border-radius: 999px;
  margin-bottom: 8px;
`;

const PaymentPlanCard = styled(PlanCard)`
  border: none;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.06);
  padding: 28px;
`;

const PlanName = styled.h4`
  font-size: 18px;
  font-weight: 700;
  color: ${({ color }) => `${color || "#333"} !important`};
  margin-bottom: 8px;
`;

const PlanDescription = styled.p`
  font-size: 13px;
  color: #444;
  margin-bottom: 16px;
  min-height: 40px;
`;

const PlanPrice = styled.div`
  font-size: 26px;
  font-weight: bold;
  color: ${({ color }) => color || "#333"};
`;

const PerMonth = styled.div`
  font-size: 13px;
  color: #99b0ad;
  margin-bottom: 12px;
`;

const SubscribeButton = styled(Button)`
  background-color: ${({ color }) => color || "#ee754c"};
  border: none;
  border-radius: 999px;
  padding: 9px 22px;
  font-weight: 700;
  font-size: 14px;
  margin: 12px auto 16px;

  &:hover {
    background-color: #ee561b;
  }
`;

const FeatureList = styled.ul`
  list-style: none;
  padding-left: 0;
  text-align: left;
  margin-top: 10px;
  margin-bottom: 0;

  li {
    display: flex;
    align-items: flex-start;
    font-size: 13px;
    color: #333;
    margin-bottom: 7px;
    font-family: "Kanit", sans-serif;

    svg {
      color: ${({ iconColor }) => iconColor || "#ee754c"};
      margin-right: 8px;
      font-size: 12px;
      margin-top: 4px;
      flex-shrink: 0;
    }
  }
`;

const PriceLine = styled.div`
  display: flex;
  align-items: baseline;
  justify-content: center;
  gap: 6px;
`;

const DiscountNote = styled.div`
  font-size: 12px;
  color: #28a745;
  font-style: italic;
  margin-top: -6px;
  margin-bottom: 10px;
`;

const PaymentTitle = styled.h3`
  font-weight: 700;
  font-size: 20px;
  margin-bottom: 16px;
  color: #333;
  text-align: center;
`;

const ViewMore = styled.button`
  background: none;
  border: none;
  color: ${(props) => props.color || "#ee754c"};
  cursor: pointer;
  font-size: 14px;
  padding: 0;
  margin-top: 6px;

  &:hover {
    text-decoration: underline;
  }
`;

const BackButton = styled(Button)`
  float: right;
  font-size: 14px;
  font-weight: 500;
  padding: 6px 14px;
  border-radius: 8px;
  background-color: transparent;
  border: 1px solid #ee754c;
  color: #ee754c;
  transition: all 0.3s ease;

  svg {
    margin-right: 6px;
  }

  &:hover {
    background-color: #e5611b;
    color: #ffffff;
    border-color: #e5611b;
  }
`;

const WarningInline = styled.div`
  margin-top: 6px;
  font-size: 13px;
  line-height: 1.35;
  color: #856404;
  background: #fff3cd;
  border: 1px solid #ffeeba;
  border-radius: 8px;
  padding: 8px 10px;
  display: inline-block;
  text-align: left;
`;

const OfferItem = styled.li`
  display: grid;
  grid-template-columns: auto 1fr;
  align-items: start;
  gap: 8px;
  margin-bottom: 8px;
  color: #333;
  font-size: 13px;

  .icon {
    margin-top: 2px;
    color: ${({ iconColor }) => iconColor || "#ee754c"};
  }
  .text {
    line-height: 1.2;
  }
  .title {
    font-weight: 700;
    margin: 0;
  }
  .desc {
    margin: 1px 0 0;
    color: #4a4a4a;
    font-size: 12px;
  }
`;

const StarterPackButton = styled.button`
  width: 100%;
  max-width: 920px;
  border: none;
  border-radius: 18px;
  padding: 16px 20px;
  font-size: 22px;
  font-weight: 800;
  color: #ffffff;
  background: linear-gradient(135deg, #ee754c 0%, #d9531e 100%);
  box-shadow: 0 12px 30px rgba(238, 117, 76, 0.28);
  cursor: pointer;
  transition:
    transform 0.2s ease,
    box-shadow 0.2s ease;
  text-align: center;
  position: sticky;
  top: 12px;
  z-index: 9;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 16px 34px rgba(238, 117, 76, 0.34);
  }

  @media (max-width: 768px) {
    font-size: 18px;
    padding: 14px 16px;
  }
`;

const StarterPackSubtext = styled.div`
  font-size: 13px;
  font-weight: 500;
  margin-top: 4px;
  opacity: 0.96;
`;

const StarterPackInfoBox = styled.div`
  width: 100%;
  background: #fff8f4;
  border: 1px solid #ffd3c1;
  border-radius: 18px;
  padding: 24px;
  box-shadow: 0 6px 18px rgba(238, 117, 76, 0.12);
`;

const StarterPackInfoTitle = styled.h4`
  font-size: 22px;
  font-weight: 800;
  color: #ee754c;
  margin-bottom: 12px;
  text-align: center;
`;

const StarterPackInfoText = styled.p`
  font-size: 15px;
  line-height: 1.5;
  color: #444;
  margin-bottom: 18px;
  text-align: center;
`;

const StarterPackSubscribeButton = styled.button`
  width: 100%;
  border: none;
  border-radius: 999px;
  padding: 14px 18px;
  font-size: 16px;
  font-weight: 800;
  color: #ffffff;
  background: #ee754c;
  box-shadow: 0 8px 20px rgba(238, 117, 76, 0.22);
  cursor: pointer;
  transition:
    transform 0.2s ease,
    box-shadow 0.2s ease,
    background-color 0.2s ease;

  &:hover {
    background: #e5611b;
    transform: translateY(-1px);
    box-shadow: 0 10px 24px rgba(238, 117, 76, 0.28);
  }

  &:disabled {
    opacity: 0.7;
    cursor: not-allowed;
  }
`;

const SeeMorePlansButton = styled.button`
  background: none;
  border: none;
  color: #666;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  padding: 0;
  margin-top: auto;
  padding-top: 10px;
  align-self: center;

  &:hover {
    color: #ee754c;
    text-decoration: underline;
  }
`;

const ComparisonSection = styled.section`
  margin-top: 10px;
  padding-top: 10px;
`;

const ComparisonHeader = styled.div`
  text-align: center;
  margin-bottom: 18px;
`;

const ComparisonTitle = styled.h3`
  font-size: 24px;
  font-weight: 800;
  color: #333;
  margin-bottom: 6px;
`;

const ComparisonSubtitle = styled.p`
  font-size: 14px;
  color: #666;
  margin: 0;
`;

const ComparisonBlock = styled.div`
  margin-bottom: 24px;
`;

const ComparisonBlockTitle = styled.h4`
  font-size: 18px;
  font-weight: 800;
  color: #ee754c;
  margin-bottom: 10px;
`;

const ComparisonTableWrap = styled.div`
  width: 100%;
  overflow-x: auto;
  border: 1px solid #ececec;
  border-radius: 16px;
  background: #fff;
`;

const ComparisonTable = styled.table`
  width: 100%;
  min-width: 760px;
  border-collapse: collapse;

  th,
  td {
    padding: 12px 14px;
    border-bottom: 1px solid #f0f0f0;
    vertical-align: top;
    text-align: center;
    font-size: 14px;
  }

  th:first-child,
  td:first-child {
    text-align: left;
    min-width: 240px;
    background: #fcfcfc;
  }

  thead th {
    background: #faf6e9;
    font-weight: 800;
    color: #333;
    position: sticky;
    top: 0;
    z-index: 1;
  }

  tbody tr:last-child td {
    border-bottom: none;
  }
`;

const TableFeatureTitle = styled.div`
  font-weight: 700;
  color: #333;
`;

const TableFeatureDesc = styled.div`
  font-size: 12px;
  color: #666;
  margin-top: 2px;
`;

const YesMark = styled.span`
  color: #28a745;
  font-weight: 800;
  font-size: 18px;
`;

const NoMark = styled.span`
  color: #c8c8c8;
  font-weight: 700;
  font-size: 18px;
`;

const PriceCell = styled.div`
  font-weight: 800;
  color: #333;
`;

const SecondaryPrice = styled.div`
  font-size: 12px;
  color: #777;
  margin-top: 4px;
`;

/* ---------- Helpers ---------- */
const splitOffersSafely = (offersString) => {
  if (!offersString) return [];
  return offersString
    .split(/\s*;\s*/g)
    .map((s) => s.trim())
    .filter(Boolean);
};

const splitTitleAndDesc = (item) => {
  const idx = item.indexOf(":");
  if (idx === -1) return { title: item.trim(), desc: "" };
  return { title: item.slice(0, idx).trim(), desc: item.slice(idx + 1).trim() };
};

const normalizeFeatureKey = (value) => value.trim().toLowerCase();

const formatMoney = (value, suffix = "") => {
  const num = Number(value ?? 0);
  if (num === 0) return "Grátis";
  return `${value} €${suffix}`;
};

const buildComparisonRows = (subscriptions) => {
  const sortedSubs = [...subscriptions].sort(
    (a, b) => a.subscription_number - b.subscription_number,
  );

  const featureMap = new Map();

  sortedSubs.forEach((sub, index) => {
    splitOffersSafely(sub.offers || "").forEach((raw) => {
      const { title, desc } = splitTitleAndDesc(raw);
      const key = normalizeFeatureKey(title);

      if (!featureMap.has(key)) {
        featureMap.set(key, {
          key,
          title,
          desc,
          introducedAtIndex: index,
        });
      }
    });
  });

  return Array.from(featureMap.values());
};

function ChoosePlanAndPay({ showHeader = true }) {
  const navigate = useNavigate();
  const params = useParams();
  const dispatch = useDispatch();
  const comparisonRef = useRef(null);

  /* Redux selectors */
  const { getSubscriptionPackages } = useSelector(
    (state) => state.subscriptionPackagesStore,
  );
  const { isLoadingConfirmSubscription } = useSelector(
    (state) => state.paymentIntent,
  );
  const restaurantDetails = useSelector((state) => state.restaurantDetails);
  const { restaurants } = restaurantDetails || {};

  const restaurant_sub = useSelector((state) => state.restaurant_subscription);
  const { restaurantSubscription } = restaurant_sub;

  const invoicesState = useSelector((s) => s.payments);
  const { upgradePaymentIntent } = invoicesState;

  /* Estado local */
  const [activeOrTrial, setActiveOrTrial] = useState(false);
  const [activeSubNumber, setActiveSubNumber] = useState(0);
  const [cadence, setCadence] = useState(null);
  const [subEndIso, setSubEndIso] = useState(null);

  const [monthlySubscriptions, setMonthlySubscriptions] = useState([]);
  const [yearlySubscriptions, setYearlySubscriptions] = useState([]);
  const [showMonthly, setShowMonthly] = useState(true);
  const [selectedSubscription, setSelectedSubscription] = useState(null);
  const [step, setStep] = useState("choose_subscription");
  const [showMoreDetails, setShowMoreDetails] = useState(false);
  const [_isConfirmationModal, _setIsConfirmationModal] = useState(false);
  const [isActivatingStarterPack, setIsActivatingStarterPack] = useState(false);

  const currentRestaurantSub = Array.isArray(restaurantSubscription)
    ? restaurantSubscription[0]
    : restaurantSubscription;

  const availableOffers = Array.isArray(currentRestaurantSub?.available_offers)
    ? currentRestaurantSub.available_offers
    : [];

  const hasStarterPack3Months = availableOffers.includes(
    "starter_pack_3_months",
  );

  const visibleSubscriptions = showMonthly
    ? monthlySubscriptions
    : yearlySubscriptions;

  const premiumMonthlySubscription =
    monthlySubscriptions.length > 0
      ? monthlySubscriptions[monthlySubscriptions.length - 1]
      : null;

  const comparisonPlans = useMemo(() => {
    if (monthlySubscriptions.length > 0) {
      return [...monthlySubscriptions].sort(
        (a, b) => a.subscription_number - b.subscription_number,
      );
    }

    return [...yearlySubscriptions].sort(
      (a, b) => a.subscription_number - b.subscription_number,
    );
  }, [monthlySubscriptions, yearlySubscriptions]);

  const yearlyByNumber = useMemo(() => {
    const map = new Map();
    yearlySubscriptions.forEach((plan) => {
      map.set(plan.subscription_number, plan);
    });
    return map;
  }, [yearlySubscriptions]);

  const comparisonRows = useMemo(
    () => buildComparisonRows(comparisonPlans),
    [comparisonPlans],
  );

  useEffect(() => {
    const status = restaurants?.subscription_status || "";
    setActiveOrTrial(["active", "trialing"].includes(status));

    setActiveSubNumber(Number(restaurants?.sub_lvl ?? 0));
    setCadence(restaurants?.yearly_or_monthly ?? null);
    setSubEndIso(restaurants?.subscription_end_date ?? null);
  }, [
    restaurants?.subscription_status,
    restaurants?.sub_lvl,
    restaurants?.yearly_or_monthly,
    restaurants?.subscription_end_date,
  ]);

  const onConfirmation = () => {
    navigate(`/reservationfromrestaurant/${params.restaurant_id}/null/all`);
  };

  useEffect(() => {
    if (step === "choose_subscription") dispatch(resetCreatePaymentIntent());
  }, [step, dispatch]);

  useEffect(() => {
    dispatch(fetchSubscriptionPackages());
    dispatch(resetConfirmSubscription());
  }, [dispatch]);

  useEffect(() => {
    if (!getSubscriptionPackages) return;

    const packages = [...getSubscriptionPackages].sort(
      (a, b) => a.subscription_number - b.subscription_number,
    );

    const monthly = packages.filter(
      (p) => Number(p.number_of_days) > 0 && Number(p.number_of_days) < 90,
    );
    const yearly = packages.filter((p) => Number(p.number_of_days) >= 300);

    setMonthlySubscriptions(monthly);
    setYearlySubscriptions(yearly);
  }, [getSubscriptionPackages]);

  useEffect(() => {
    if (cadence === "monthly") setShowMonthly(true);
    else if (cadence === "yearly") setShowMonthly(false);
  }, [cadence]);

  const handleSelectSubscription = (subscription) => {
    const targetSubscription =
      hasStarterPack3Months && premiumMonthlySubscription
        ? premiumMonthlySubscription
        : subscription;

    if (!targetSubscription) return;

    const isCurrent =
      activeOrTrial &&
      targetSubscription.subscription_number === Number(activeSubNumber);

    if (isCurrent) return;

    if (!hasStarterPack3Months && activeOrTrial && restaurants?.id) {
      dispatch(
        createUpgradeIntent({
          restaurantId: restaurants.id,
          body: {
            new_subscription_number: targetSubscription.subscription_number,
          },
        }),
      );
    }

    setSelectedSubscription(targetSubscription);
    setShowMoreDetails(false);
    setStep("payment");
  };

  const handleSetConfirmation = useCallback((val) => {
    _setIsConfirmationModal(val);
  }, []);

  const handleStarterPackSubscribe = async () => {
    setIsActivatingStarterPack(true);

    try {
      await dispatch(
        activateStarterPack({
          restaurantId: params.restaurant_id,
        }),
      );

      await dispatch(
        fetchSubscriptionStatus({ restaurant_id: params.restaurant_id }),
      );

      _setIsConfirmationModal(true);
    } catch (error) {
      console.error("Erro ao ativar starter pack:", error);
      alert(error.message || "Não foi possível ativar a oferta.");
    } finally {
      setIsActivatingStarterPack(false);
    }
  };

  const handleScrollToComparison = () => {
    comparisonRef.current?.scrollIntoView({
      behavior: "smooth",
      block: "start",
    });
  };

  const fmtDate = (iso) => {
    try {
      const d = new Date(iso);
      return isNaN(d) ? "" : d.toLocaleDateString("pt-PT");
    } catch {
      return "";
    }
  };

  const isStarterPackFlow =
    hasStarterPack3Months &&
    premiumMonthlySubscription &&
    selectedSubscription &&
    Number(selectedSubscription.subscription_number) ===
      Number(premiumMonthlySubscription.subscription_number);

  const isSelectedYearToMonth =
    !isStarterPackFlow &&
    cadence === "yearly" &&
    selectedSubscription?.number_of_days === 30;

  const isSelectedDowngrade =
    !isStarterPackFlow &&
    activeOrTrial &&
    selectedSubscription &&
    Number(selectedSubscription.subscription_number) < activeSubNumber;

  const displayAmount =
    activeOrTrial &&
    !isSelectedDowngrade &&
    !isSelectedYearToMonth &&
    upgradePaymentIntent?.amount_cents != null
      ? (upgradePaymentIntent.amount_cents * 0.01).toFixed(2)
      : (selectedSubscription?.total_value ?? 0);

  const renderComparisonTable = (plans, rows) => {
    if (!plans.length) return null;

    return (
      <ComparisonBlock>
        <ComparisonBlockTitle>Comparação de planos</ComparisonBlockTitle>

        <ComparisonTableWrap>
          <ComparisonTable>
            <thead>
              <tr>
                <th>Funcionalidade</th>
                {plans.map((plan) => (
                  <th key={`comparison-${plan.subscription_number}`}>
                    {plan.name}
                  </th>
                ))}
              </tr>
            </thead>

            <tbody>
              <tr>
                <td>
                  <TableFeatureTitle>Preço mensal</TableFeatureTitle>
                  <TableFeatureDesc>Cobrança mensal</TableFeatureDesc>
                </td>

                {plans.map((plan) => (
                  <td key={`monthly-price-${plan.subscription_number}`}>
                    <PriceCell>
                      {formatMoney(plan.monthly_value, " / mês")}
                    </PriceCell>
                  </td>
                ))}
              </tr>

              <tr>
                <td>
                  <TableFeatureTitle>Preço anual</TableFeatureTitle>
                  <TableFeatureDesc>
                    Cobrança anual com equivalente mensal
                  </TableFeatureDesc>
                </td>

                {plans.map((plan) => {
                  const yearlyPlan = yearlyByNumber.get(
                    plan.subscription_number,
                  );

                  return (
                    <td key={`yearly-price-${plan.subscription_number}`}>
                      {yearlyPlan ? (
                        <>
                          <PriceCell>
                            {formatMoney(yearlyPlan.total_value, " / ano")}
                          </PriceCell>
                          <SecondaryPrice>
                            {formatMoney(yearlyPlan.monthly_value, " / mês")}
                          </SecondaryPrice>
                        </>
                      ) : (
                        <NoMark>—</NoMark>
                      )}
                    </td>
                  );
                })}
              </tr>

              {rows.map((feature) => (
                <tr key={`feature-${feature.key}`}>
                  <td>
                    <TableFeatureTitle>{feature.title}</TableFeatureTitle>
                    {feature.desc ? (
                      <TableFeatureDesc>{feature.desc}</TableFeatureDesc>
                    ) : null}
                  </td>

                  {plans.map((plan, planIndex) => {
                    const hasFeature = planIndex >= feature.introducedAtIndex;

                    return (
                      <td
                        key={`feature-${feature.key}-${plan.subscription_number}`}
                      >
                        {hasFeature ? <YesMark>✔</YesMark> : <NoMark>—</NoMark>}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </ComparisonTable>
        </ComparisonTableWrap>
      </ComparisonBlock>
    );
  };

  const renderPacks = (subscription) => {
    const color = getPlanColor(subscription.subscription_number);
    const isCurrent =
      activeOrTrial &&
      subscription.subscription_number === Number(activeSubNumber);

    const allOffers = splitOffersSafely(subscription.offers || "");
    const shortOffers = allOffers.slice(0, FEATURE_PREVIEW_COUNT);
    const isZeroPrice = Number(subscription?.monthly_value ?? 0) === 0;

    return (
      <Col
        sm={12}
        md={4}
        key={subscription.subscription_number}
        className="mb-4 d-flex"
      >
        <PlanCard $selected={isCurrent}>
          {isCurrent && <ActiveBadge>Plano atual</ActiveBadge>}

          <PlanName color={color}>{subscription.name}</PlanName>
          <PlanDescription>{subscription.description}</PlanDescription>

          <PriceLine>
            <PlanPrice color={color}>
              {isZeroPrice ? "Grátis" : `${subscription.monthly_value} €`}
            </PlanPrice>
            <PerMonth>{isZeroPrice ? null : "/ mês"}</PerMonth>
          </PriceLine>

          {subscription.number_of_days === 365 && (
            <DiscountNote>
              *Inclui 20% de desconto em relação ao mensal
            </DiscountNote>
          )}

          <SubscribeButton
            color={color}
            onClick={() => handleSelectSubscription(subscription)}
            disabled={isCurrent}
          >
            {isCurrent
              ? "Plano atual"
              : hasStarterPack3Months
                ? "Experimente grátis"
                : "Subscrever"}
          </SubscribeButton>

          {shortOffers.length > 0 && (
            <FeatureList as="ul" iconColor={color}>
              {shortOffers.map((raw, idx) => {
                const { title, desc } = splitTitleAndDesc(raw);
                return (
                  <OfferItem key={idx} iconColor={color}>
                    <span className="icon">
                      <FontAwesomeIcon icon={faCircleCheck} />
                    </span>
                    <div className="text">
                      <div className="title">{title}</div>
                      {desc && <div className="desc">{desc}</div>}
                    </div>
                  </OfferItem>
                );
              })}
            </FeatureList>
          )}

          <SeeMorePlansButton onClick={handleScrollToComparison}>
            + ver mais
          </SeeMorePlansButton>
        </PlanCard>
      </Col>
    );
  };

  return (
    <Container>
      {step === "choose_subscription" ? (
        <>
          <Row>
            {showHeader && (
              <CardHeader>
                <CardHeaderTitle>Plano</CardHeaderTitle>
              </CardHeader>
            )}

            {activeOrTrial && restaurants?.id && !hasStarterPack3Months && (
              <Col xs={12}>
                <BackButton
                  onClick={() =>
                    navigate(`/subscription_management/${restaurants.id}`)
                  }
                >
                  <FontAwesomeIcon icon={faCircleLeft} />
                  Gerir plano
                </BackButton>
              </Col>
            )}

            <Col xs={12} className="d-flex justify-content-center mb-4 gap-2">
              <ButtonPlans
                isSelected={showMonthly}
                onClick={() => setShowMonthly(true)}
              >
                Mensal
              </ButtonPlans>
              <ButtonPlans
                isSelected={!showMonthly}
                onClick={() => setShowMonthly(false)}
              >
                Anual
              </ButtonPlans>
            </Col>

            {hasStarterPack3Months && premiumMonthlySubscription && (
              <Col xs={12} className="mb-4 d-flex justify-content-center">
                <StarterPackButton
                  onClick={() =>
                    handleSelectSubscription(premiumMonthlySubscription)
                  }
                >
                  Experimente grátis o plano completo durante 3 meses.
                  <StarterPackSubtext>
                    Oferta ativa. Ao clicar em qualquer plano, será sempre
                    selecionado o plano Completo.
                  </StarterPackSubtext>
                </StarterPackButton>
              </Col>
            )}

            <Col xs={12}>
              <Row>{visibleSubscriptions.map((sub) => renderPacks(sub))}</Row>
            </Col>
          </Row>

          <ComparisonSection ref={comparisonRef}>
            <ComparisonHeader>
              <ComparisonTitle>Comparação de planos</ComparisonTitle>
              <ComparisonSubtitle>
                Veja preços mensais, anuais e todas as funcionalidades.
              </ComparisonSubtitle>
            </ComparisonHeader>

            {renderComparisonTable(comparisonPlans, comparisonRows)}
          </ComparisonSection>
        </>
      ) : !isLoadingConfirmSubscription ? (
        <>
          {showHeader && (
            <CardHeader>
              <CardHeaderTitle>Plano</CardHeaderTitle>
            </CardHeader>
          )}

          <Row className="mb-2">
            <Col xs={12}>
              <BackButton onClick={() => setStep("choose_subscription")}>
                <FontAwesomeIcon icon={faCircleLeft} />
                Voltar
              </BackButton>
            </Col>
          </Row>

          <Row className="align-items-start">
            <Col xs={12} md={6} className="mb-4">
              {selectedSubscription && (
                <PaymentPlanCard>
                  {(() => {
                    const color = getPlanColor(
                      selectedSubscription.subscription_number,
                    );
                    const endDate = subEndIso ? fmtDate(subEndIso) : null;

                    const fullOffers = splitOffersSafely(
                      selectedSubscription.offers || "",
                    );
                    const previewOffers = FEATURE_PREVIEW_COUNT
                      ? fullOffers.slice(0, FEATURE_PREVIEW_COUNT)
                      : fullOffers;

                    const canToggle =
                      FEATURE_PREVIEW_COUNT &&
                      fullOffers.length > FEATURE_PREVIEW_COUNT;

                    return (
                      <>
                        <PaymentTitle>
                          {isStarterPackFlow ? "Ativar oferta" : "Pagamento"}
                        </PaymentTitle>

                        <PlanName color={color}>
                          {selectedSubscription.name}
                        </PlanName>

                        <PlanDescription>
                          {isStarterPackFlow
                            ? "Plano completo com acesso total às funcionalidades."
                            : `Plano ${
                                selectedSubscription.number_of_days === 365
                                  ? "anual"
                                  : "mensal"
                              } com funcionalidades essenciais para o seu restaurante.`}
                        </PlanDescription>

                        {isStarterPackFlow ? (
                          <DiscountNote>
                            3 meses grátis sem qualquer compromisso
                          </DiscountNote>
                        ) : hasStarterPack3Months ? (
                          <DiscountNote>
                            Oferta ativa: Completo tudo incluído 3 meses grátis
                          </DiscountNote>
                        ) : null}

                        <PriceLine>
                          <PlanPrice color={color}>
                            {isStarterPackFlow
                              ? "Grátis"
                              : `${displayAmount} €`}
                          </PlanPrice>

                          {!isStarterPackFlow &&
                            selectedSubscription.number_of_days === 365 && (
                              <PerMonth>
                                {Number(
                                  selectedSubscription.monthly_value ?? 0,
                                ) === 0
                                  ? "Grátis"
                                  : selectedSubscription.monthly_value}
                                {" € / mês"}
                              </PerMonth>
                            )}
                        </PriceLine>

                        {(isSelectedYearToMonth || isSelectedDowngrade) && (
                          <WarningInline>
                            {isSelectedYearToMonth ? (
                              <>
                                Está a alterar de <b>anual</b> para{" "}
                                <b>mensal</b>. O novo preço mensal será aplicado{" "}
                                {endDate ? (
                                  <>
                                    no fim do período atual (<b>{endDate}</b>).
                                  </>
                                ) : (
                                  <>no fim do período atual.</>
                                )}{" "}
                                Não será cobrado agora, mas pode atualizar o
                                método de pagamento abaixo.
                              </>
                            ) : (
                              <>
                                Está a reduzir o plano. A alteração entra em
                                vigor no fim do período atual{" "}
                                {endDate && (
                                  <>
                                    (<b>{endDate}</b>)
                                  </>
                                )}
                                . Não será cobrado agora, mas pode atualizar o
                                método de pagamento abaixo.
                              </>
                            )}
                          </WarningInline>
                        )}

                        {!isStarterPackFlow &&
                          selectedSubscription.number_of_days === 365 && (
                            <DiscountNote>
                              *Inclui 20% de desconto em relação ao mensal
                            </DiscountNote>
                          )}

                        {!showMoreDetails ? (
                          <>
                            <FeatureList as="ul" iconColor={color}>
                              {previewOffers.map((raw, idx) => {
                                const { title, desc } = splitTitleAndDesc(raw);
                                return (
                                  <OfferItem key={idx} iconColor={color}>
                                    <span className="icon">
                                      <FontAwesomeIcon icon={faCircleCheck} />
                                    </span>
                                    <div className="text">
                                      <div className="title">{title}</div>
                                      {desc && (
                                        <div className="desc">{desc}</div>
                                      )}
                                    </div>
                                  </OfferItem>
                                );
                              })}
                            </FeatureList>

                            {canToggle && (
                              <ViewMore
                                color={color}
                                onClick={() => setShowMoreDetails(true)}
                              >
                                Ver mais
                              </ViewMore>
                            )}
                          </>
                        ) : (
                          <>
                            <FeatureList as="ul" iconColor={color}>
                              {fullOffers.map((raw, idx) => {
                                const { title, desc } = splitTitleAndDesc(raw);
                                return (
                                  <OfferItem key={idx} iconColor={color}>
                                    <span className="icon">
                                      <FontAwesomeIcon icon={faCircleCheck} />
                                    </span>
                                    <div className="text">
                                      <div className="title">{title}</div>
                                      {desc && (
                                        <div className="desc">{desc}</div>
                                      )}
                                    </div>
                                  </OfferItem>
                                );
                              })}
                            </FeatureList>

                            {canToggle && (
                              <ViewMore
                                color={color}
                                onClick={() => setShowMoreDetails(false)}
                              >
                                Ver menos
                              </ViewMore>
                            )}
                          </>
                        )}
                      </>
                    );
                  })()}
                </PaymentPlanCard>
              )}
            </Col>

            <Col xs={12} md={6} className="mb-4">
              {selectedSubscription &&
                (isStarterPackFlow ? (
                  <StarterPackInfoBox>
                    <StarterPackInfoTitle>
                      Ative o seu período gratuito
                    </StarterPackInfoTitle>

                    <StarterPackInfoText>
                      Ao clicar <b>Subscrever</b> vai ativar o plano{" "}
                      <b>Completo</b> durante <b>3 meses</b> sem qualquer
                      compromisso.
                    </StarterPackInfoText>

                    <StarterPackSubscribeButton
                      onClick={handleStarterPackSubscribe}
                      disabled={isActivatingStarterPack}
                    >
                      {isActivatingStarterPack ? "A ativar..." : "Subscrever"}
                    </StarterPackSubscribeButton>
                  </StarterPackInfoBox>
                ) : (
                  <>
                    <StripeCreatePayMethod
                      subscription_number={
                        selectedSubscription.subscription_number
                      }
                      restaurant_id={params.restaurant_id}
                      handleSetConfirmation={handleSetConfirmation}
                      isUpdate={activeOrTrial}
                    />
                    <p
                      style={{
                        textAlign: "center",
                        color: "#555",
                        fontSize: "12px",
                        marginTop: "12px",
                      }}
                    >
                      Todos os planos são renovados automaticamente.
                      <br />
                      Pode cancelar a qualquer momento.
                    </p>
                  </>
                ))}
            </Col>
          </Row>
        </>
      ) : (
        <LoaderComponent activate={isLoadingConfirmSubscription} />
      )}

      <ConfirmationModal
        show={_isConfirmationModal}
        onHide={() => _setIsConfirmationModal(false)}
        onConfirm={onConfirmation}
        onCancel={() => _setIsConfirmationModal(false)}
        title={
          isStarterPackFlow ? "Plano ativado" : "Dados de pagamento registados"
        }
        body={
          isStarterPackFlow
            ? "O plano Completo foi ativado com sucesso durante 3 meses grátis. Já pode começar a gerir as suas reservas."
            : "Os dados de pagamento foram registados com sucesso. Já podes começar a gerir as tuas reservas."
        }
        confirmLabel="Começar a gerir"
        confirmColor="#ee754c"
        centered={true}
      />
    </Container>
  );
}

export default ChoosePlanAndPay;
